import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { ContasPagarAction, useContasPagar } from 'app/main/contaspagar/store';
import contasPagarApi from 'app/services/api/contasPagarApi';
import * as Actions from 'app/store/actions';
import * as MessageActions from 'app/store/actions/fuse/message.actions';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ConciliacaoContext } from '../ConciliacaoContext';
import CadastrarContaPagarModal from '../modal/CadastrarContaPagarModal';
import { ConciliacaoModal } from '../modal/ConciliacaoModal';
import ContentLancamentoDespesa from './ContentLancamentoDespesa';
import ContentLancamentoReceita from './ContentLancamentoReceita';
import ContentLancamentoTransferencia from './ContentLancamentoTransferencia';

const ContentLancamento = ({ extrato, handlerConciliar, handlerDesconciliar, index }) => {
  const [loading, setLoading] = useState(false);
  const [, dispatch] = useContasPagar();
  const [modal, setModal] = useContext(ConciliacaoContext).modal;
  const [, setConciliacao] = useContext(ConciliacaoContext).conciliacao;
  const [, setContaPagar] = useContext(ConciliacaoContext).contaPagar;

  useReduxLoading(loading);

  const dispatchMessage = useDispatch();
  const apiUtils = useApiUtils(dispatchMessage, Actions);

  const showError = (message) => {
    dispatchMessage(MessageActions.showMessage({ message, variant: 'error' }));
  };

  const handlerVisualizarDespesa = async (despesa) => {
    setLoading(true);
    const ids = [];
    let state = {};
    if (despesa?.parcelamentoDespesaOrigemId) {
      ids.push(despesa?.parcelamentoDespesaOrigemId);
      state = {
        ...state,
        numeroParcelas: despesa?.numeroParcelas,
        parcela: despesa?.parcela,
        contaPagarParcela: {},
      };
    } else if (despesa?.retencaoDespesaOrigemId) {
      ids.push(despesa?.retencaoDespesaOrigemId);
      ids.push(despesa.id);
      state = {
        ...state,
        retencaoId: despesa.id,
      };
    } else if (despesa?.linhaDigitavel) {
      ids.push(despesa.id);
      state = {
        ...state,
        linhaDigitavel: despesa.linhaDigitavel,
      };
    } else {
      ids.push(despesa.id);
    }
    await ContasPagarAction.requestVisualizar(dispatch, ids, state);
    setLoading(false);
  };

  const abrirModalDespesaUnica = () => {
    setConciliacao(extrato);
    setModal(ConciliacaoModal.cadastrarContaPagarModal);
  };

  const editarContaPagar = async (id) => {
    setLoading(true);
    const conta = await fetchContaPagar(id);
    if (conta?.possuiRetencao || conta?.retencaoDespesaOrigemId) {
      showError(
        'Não é possível editar despesa com imposto retido na Conciliação, caso queira alterar, vá para o "Contas a pagar".'
      );

      return false;
    }
    if (conta?.parcelamentoDespesaOrigemId) {
      showError(
        'Não é possível editar despesa parcelada na Conciliação, caso queira alterar, vá para o "Contas a pagar".'
      );
      return false;
    }
    abrirModalDespesaUnica();
    setLoading(false);
  };

  const handlerEditarDespesa = async (contaPagar) => {
    if (!contaPagar.possuiRetencoes) {
      if (!contaPagar.baixa) {
        editarContaPagar(contaPagar.id);
      } else {
        showError('Não é possível editar a despesa pois ela está baixada.');
      }
    } else {
      showError(
        'Não é possível editar despesa com imposto retido na Conciliação, caso queira alterar, vá para o "Contas a pagar".'
      );
    }
  };

  const handlerLancarDespesa = () => {
    abrirModalDespesaUnica();
  };

  const fetchContaPagar = async (id) => {
    try {
      setLoading(true);
      const response = await contasPagarApi.buscarPorId(id);

      const { data } = response;

      if (data?.dataEmissao) {
        data.dataEmissao = moment(data.dataEmissao);
      }

      if (data?.dataVencimento) {
        data.dataVencimento = moment(data.dataVencimento);
      }

      if (data?.parcelamento?.parcelas?.length) {
        data.parcelamento.parcelas = data.parcelamento.parcelas.map((parcela) => ({
          ...parcela,
          dataVencimento: moment(parcela.dataVencimento),
          informarCodigoBarras: !!parcela.linhaDigitavel,
        }));
        data.numeroParcelas = data.parcelamento.parcelas.length;
      }

      if (data?.retencoes?.length) {
        data.retencoes = data.retencoes.map((retencao) => ({
          ...retencao,
          dataVencimento: moment(retencao.dataVencimento),
        }));
      }

      if (data.tipoDespesa === 'LINHA_DIGITAVEL' || data.tipoDespesa === 'PARCELADA') {
        data.valorTotal = data.valorBruto;
      }
      setContaPagar(data);
      return data;
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ContentLancamentoDespesa
        index={index}
        extrato={extrato}
        handlerVisualizarDespesa={handlerVisualizarDespesa}
        handlerEditarDespesa={handlerEditarDespesa}
        handlerConciliar={handlerConciliar}
        handlerDesconciliar={handlerDesconciliar}
      />
      <ContentLancamentoReceita
        index={index}
        extrato={extrato}
        handlerVisualizarDespesa={handlerVisualizarDespesa}
        handlerEditarDespesa={handlerEditarDespesa}
        handlerConciliar={handlerConciliar}
        handlerDesconciliar={handlerDesconciliar}
        handlerLancarContaPagar={handlerLancarDespesa}
      />
      <ContentLancamentoTransferencia
        index={index}
        extrato={extrato}
        handlerConciliar={handlerConciliar}
        handlerDesconciliar={handlerDesconciliar}
      />

      {modal === ConciliacaoModal.cadastrarContaPagarModal && <CadastrarContaPagarModal origemConciliacao />}
    </>
  );
};

export default ContentLancamento;
